import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useIntl } from '@leagueplatform/locales';
import type { EnvironmentKey, ConfigEntityDetail } from '@web-config-app/core';
import { ConfigEntityAuthAction } from '@web-config-app/core';
import { buildConfigEntityAuthHeaders } from '@web-config-app/api';
import { useEnvironmentFetch } from '../use-environment-fetch/use-environment-fetch';
import { useAbortController } from '../use-abort-controller/use-abort-controller';
import type { BulkActionReportingStore } from '../../bulk-action-reporting/bulk-action-reporting.store';
import { createBulkActionReporting } from '../../bulk-action-reporting/bulk-action-reporting-integration';

export type UseBulkDeleteEntitiesOptions = {
  currentAuthoringEnvironment: EnvironmentKey;
  domainEntityType: string;
  bulkActionReportingStore?: BulkActionReportingStore;
};

export const useBulkDeleteEntities = ({
  currentAuthoringEnvironment,
  domainEntityType,
  bulkActionReportingStore,
}: UseBulkDeleteEntitiesOptions) => {
  const { formatMessage } = useIntl();
  const environmentFetch = useEnvironmentFetch(currentAuthoringEnvironment);
  const queryClient = useQueryClient();
  const { createAbortController, abort: cancelDelete } = useAbortController();

  const handleBulkDeleteEntities = useCallback(
    async (selectedEntities: ConfigEntityDetail[]) => {
      const abortController = createAbortController();
      const entityCount = selectedEntities.length;
      const reporting = createBulkActionReporting(bulkActionReportingStore, {
        operationType: 'delete',
        domainEntityType,
        sourceEnvironment: currentAuthoringEnvironment,
      });

      reporting.start(selectedEntities);

      try {
        const response = await environmentFetch('/v1/config-entity-deletions', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/vnd.api+json',
            ...buildConfigEntityAuthHeaders(
              domainEntityType,
              ConfigEntityAuthAction.Delete,
            ),
          },
          signal: abortController.signal,
          body: JSON.stringify({
            data: {
              id: crypto.randomUUID(),
              type: 'config-entity-deletion',
              attributes: {
                entries: selectedEntities.map((entity) => ({
                  id: entity.id,
                  type: 'config-entity',
                  attributes: {
                    entityMetadata: {
                      domainEntityType,
                    },
                  },
                })),
              },
            },
          }),
        });

        if (!response.ok) {
          throw new Error(`Failed to delete entities: ${response.statusText}`);
        }

        const result = await response.json();
        const operationResults: Array<{
          id: string;
          meta: { operationStatus: string };
        }> = result?.data?.attributes?.results ?? [];
        const failedEntityIds = operationResults
          .filter((r) => r.meta.operationStatus === 'failed')
          .map((r) => r.id);
        const succeededCount = entityCount - failedEntityIds.length;

        reporting.finish(
          selectedEntities,
          failedEntityIds,
          formatMessage({ id: 'BULK_OPERATION_ERROR_MESSAGE' }),
        );

        if (succeededCount > 0) {
          queryClient.invalidateQueries({
            queryKey: [currentAuthoringEnvironment],
          });
        }
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          return;
        }

        reporting.fail(formatMessage({ id: 'BULK_OPERATION_ERROR_MESSAGE' }));
      }
    },
    [
      createAbortController,
      domainEntityType,
      environmentFetch,
      queryClient,
      formatMessage,
      currentAuthoringEnvironment,
      bulkActionReportingStore,
    ],
  );

  return { handleBulkDeleteEntities, cancelDelete };
};
